import _ from 'lodash'
import { createAction, createEntityAdapter, createSlice, PayloadAction } from '@reduxjs/toolkit'

import consultationModel, {
  Act,
  addBiologicalDto,
  Consultation,
  ConsultationReason,
  ConsultationState,
  CreateConsultationDto,
  CreateConsultationReasonDto,
  ICalendso,
  SearchedConsultationParticipant,
  TwilioStunServer,
  UpdateConsultationDto,
  UpdateConsultationReasonDto,
} from '@/core/models/consultation.model'
import { CalendsoEventType, Staff } from '@/core/models/staff.model'
import { InviteParticipantRequestParams, JoinMeetingRequestParams, SearchRequest, SearchRequestOptions } from '@/core/types/request'

import { RootState } from '@/redux/rootReducer'
import { consultationApi } from '@/core/api/consultation'

/**
 * Preserve populated sub-documents (medecin, pds, patient) when incoming
 * socket data contains bare string ObjectIds instead of full objects.
 * Without this, `consultationAdapter.updateOne` would overwrite the populated
 * object with a bare string, causing `foo.userInfo.gender` crashes.
 */
function mergePopulatedField(incoming: any, existing: any) {
  if (!incoming || typeof incoming !== 'object') {
    return existing ?? incoming
  }
  if (!incoming.userInfo && existing?.userInfo) {
    return { ...incoming, userInfo: existing.userInfo }
  }
  return incoming
}

function safeMergeConsultation(incoming: Consultation, existing: Consultation | undefined): Consultation {
  if (!existing) return incoming
  return {
    ...incoming,
    medecin: mergePopulatedField(incoming.medecin, existing.medecin) as any,
    pds: mergePopulatedField(incoming.pds, existing.pds) as any,
    patient: mergePopulatedField(incoming.patient, existing.patient) as any,
  }
}

export type ConsultationReducerState = ConsultationState & {
  patientConsultations: Record<string, { items: Consultation[]; totalCount: number }>
  searchParticipants: {
    result: {
      items: SearchedConsultationParticipant[]
      total: number
    }
  }
  twilioStunServer?: TwilioStunServer
}

export const consultationReasonAdapter = createEntityAdapter<ConsultationReason, ConsultationReason['_id']>({
  selectId: (consultationReason) => consultationReason._id,
})
const consultationReasonSelector = consultationReasonAdapter.getSelectors<RootState>((state) => state.consultation.consultationReasons)

export const consultationAdapter = createEntityAdapter<Consultation, Consultation['_id']>({ selectId: (consultation) => consultation._id })
const consultationSelector = consultationAdapter.getSelectors<RootState>((state) => state.consultation.consultations)

export const selectConsultationReasons = consultationReasonSelector.selectAll
export const selectConsultations = consultationSelector.selectAll
export const selectConsultationById = consultationSelector.selectById
export const selectAttendeeInfo = (state: RootState, meetingType: string) => state.consultation.chimeMeetings[meetingType]?.attendeeInfo || null
export const selectMeetingInfo = (state: RootState, meetingType: string) => state.consultation.chimeMeetings[meetingType]?.meetingInfo || null
export const selectRpiMeetingId = (state: RootState) => state.consultation.rpiMeetingId
export const selectTwilioStunServer = (state: RootState) => state.consultation.twilioStunServer
export const selectConsultationSearchParams = (state: RootState) => state.consultation.search.params
export const selectConsultationSearchResult = (state: RootState) => state.consultation.search.result
export const selectNewlyCreatedConsultationId = (state: RootState) => state.consultation.newlyCreatedItemId
export const selectParticipantsByConsultationId = (state: RootState, consultationId: string) =>
  state.consultation.participantsByConsultationId[consultationId] || []
export const selectPatientConsultations = (state: RootState, patientId: string) => state.consultation.patientConsultations[patientId]?.items || []
export const selectSearchParticipantsResult = (state: RootState) => state.consultation.searchParticipants.result

export const selectConsultationGetRequestTracker = (state: RootState) => state.consultation.requestTracker.get
export const selectConsultationCreateRequestTracker = (state: RootState) => state.consultation.requestTracker.create
export const selectConsultationUpdateRequestTracker = (state: RootState) => state.consultation.requestTracker.update
export const selectConsultationInviteRequestTracker = (state: RootState) => state.consultation.requestTracker.invite
export const selectConsultationReasonCreateRequestTracker = (state: RootState) => state.consultation.requestTracker.createConsultationReason
export const selectConsultationReasonListRequestTracker = (state: RootState) => state.consultation.requestTracker.listConsultationReasons
export const selectConsultationReasonUpdateRequestTracker = (state: RootState) => state.consultation.requestTracker.updateConsultationReason
export const selectConsultationReasonDeleteRequestTracker = (state: RootState) => state.consultation.requestTracker.deleteConsultationReason

const initialState: ConsultationReducerState = _.merge({}, consultationModel.getInitialState(), {
  patientConsultations: {},
  requestTracker: {
    createConsultationReason: { error: null, loading: false },
    listConsultationReasons: { error: null, loading: false },
    updateConsultationReason: { error: null, loading: false },
    deleteConsultationReason: { error: null, loading: false },
    getRpiMeetingId: { error: null, loading: false },
    searchParticipants: { error: null, loading: false },
    sendChat: {
      error: null,
      loading: false,
    },
  },
  searchParticipants: {
    result: {
      items: [],
      total: 0,
    },
  },
})

const consultationSlice = createSlice({
  name: consultationModel.reducerName,
  initialState,
  reducers: {
    clearNewlyCreatedConsultationId(state) {
      state.newlyCreatedItemId = undefined
    },
    createConsultation(state, action: PayloadAction<{ consultation: CreateConsultationDto; eventType?: CalendsoEventType }>) {
      state.requestTracker.create.loading = true
    },
    createConsultationSuccess(state, action: PayloadAction<Consultation>) {
      consultationAdapter.addOne(state.consultations, action.payload)
      state.requestTracker.create.error = null
      state.requestTracker.create.loading = false
      state.newlyCreatedItemId = action.payload._id
    },
    createConsultationFailure(state, action) {
      state.requestTracker.create.error = action.payload
      state.requestTracker.create.loading = false
    },
    createConsultationReason(state, action: PayloadAction<CreateConsultationReasonDto>) {
      state.requestTracker.createConsultationReason.loading = true
    },
    createConsultationReasonSuccess(state, action: PayloadAction<ConsultationReason>) {
      consultationReasonAdapter.setOne(state.consultationReasons, action.payload)
      state.requestTracker.createConsultationReason.error = null
      state.requestTracker.createConsultationReason.loading = false
    },
    createConsultationReasonFailure(state, action) {
      state.requestTracker.createConsultationReason.error = action.payload
      state.requestTracker.createConsultationReason.loading = false
    },
    deleteConsultationReason(state, action: PayloadAction<string>) {
      state.requestTracker.deleteConsultationReason.loading = true
    },
    deleteConsultationReasonSuccess(state, action: PayloadAction<string>) {
      consultationReasonAdapter.removeOne(state.consultationReasons, action.payload)
      state.requestTracker.deleteConsultationReason.error = null
      state.requestTracker.deleteConsultationReason.loading = false
    },
    deleteConsultationReasonFailure(state, action) {
      state.requestTracker.deleteConsultationReason.error = action.payload
      state.requestTracker.deleteConsultationReason.loading = false
    },
    endConsultation(state, action: PayloadAction<string>) {
      state.requestTracker.update.loading = true
    },
    endConsultationSuccess(state, action: PayloadAction<Consultation>) {
      const consultation = action.payload
      const existing = state.consultations.entities[consultation._id]
      const merged = safeMergeConsultation(consultation, existing)
      consultationAdapter.updateOne(state.consultations, { id: consultation._id, changes: merged })
      state.requestTracker.update.error = null
      state.requestTracker.update.loading = false
    },
    endConsultationFailure(state, action) {
      state.requestTracker.update.error = action.payload
      state.requestTracker.update.loading = false
    },
    getConsultationReasons(state, action: PayloadAction<SearchRequest | undefined>) {
      state.requestTracker.listConsultationReasons.loading = true
    },
    getConsultationReasonsSuccess(state, action: PayloadAction<ConsultationReason[]>) {
      consultationReasonAdapter.setAll(state.consultationReasons, action.payload)
      state.requestTracker.listConsultationReasons.error = null
      state.requestTracker.listConsultationReasons.loading = false
    },
    getConsultationReasonsFailure(state, action) {
      state.requestTracker.listConsultationReasons.error = action.payload
      state.requestTracker.listConsultationReasons.loading = false
    },
    getConsultation(state, action: PayloadAction<string>) {
      state.requestTracker.get.loading = true
    },
    getConsultationSuccess(state, action) {
      if (action.payload && action.payload._id) {
        consultationAdapter.upsertOne(state.consultations, action.payload)
      }
      state.requestTracker.get.error = null
      state.requestTracker.get.loading = false
    },
    getConsultationFailure(state, action) {
      state.requestTracker.get.loading = false
      state.requestTracker.get.error = action.payload
    },
    getConsultations: {
      reducer: (state, action: PayloadAction<{ query?: Record<string, any>; options?: SearchRequestOptions }>) => {
        state.search.params.query = action.payload.query
        state.search.params.options = action.payload.options
        state.requestTracker.list.loading = true
      },
      prepare: (query?: Record<string, any>, options?: SearchRequestOptions) => ({
        payload: {
          query,
          options,
        },
      }),
    },
    getConsultationsSuccess(state, action: PayloadAction<{ items: Consultation[]; totalCount: number }>) {
      const { items, totalCount } = action.payload
      consultationAdapter.setAll(state.consultations, items)
      state.search.result.totalCount = totalCount
      state.requestTracker.list.error = null
      state.requestTracker.list.loading = false
    },
    getConsultationsFailure(state, action) {
      state.requestTracker.list.error = action.payload
      state.requestTracker.list.loading = false
    },
    getRpiMeetingId(state) {
      state.requestTracker.getRpiMeetingId.loading = true
    },
    getRpiMeetingIdSuccess(state, action: PayloadAction<string | null>) {
      state.rpiMeetingId = action.payload
      state.requestTracker.getRpiMeetingId.error = null
      state.requestTracker.getRpiMeetingId.loading = false
    },
    getRpiMeetingIdFailure(state, action) {
      state.rpiMeetingId = null
      state.requestTracker.getRpiMeetingId.error = action.payload
      state.requestTracker.getRpiMeetingId.loading = false
    },
    getStunServer() { },
    setStunServer(state, action: PayloadAction<TwilioStunServer>) {
      state.twilioStunServer = action.payload
    },
    joinMeeting(state, action: PayloadAction<JoinMeetingRequestParams>) {
      state.requestTracker.joinMeeting.loading = true
    },
    joinMeetingSuccess(state, action: PayloadAction<{ meetingType: string; attendeeInfo: Record<string, any>; meetingInfo: Record<string, any> }>) {
      const { attendeeInfo, meetingInfo, meetingType } = action.payload
      state.chimeMeetings[meetingType] = state.chimeMeetings[meetingType] || {}
      state.chimeMeetings[meetingType].attendeeInfo = attendeeInfo
      state.chimeMeetings[meetingType].meetingInfo = meetingInfo
      state.requestTracker.joinMeeting.error = null
      state.requestTracker.joinMeeting.loading = false
    },
    joinMeetingFailure(state, action) {
      state.requestTracker.joinMeeting.error = action.payload
      state.requestTracker.joinMeeting.loading = false
    },
    leaveMeeting(state, action: PayloadAction<string>) {
      if (state.chimeMeetings[action.payload]) {
        state.chimeMeetings[action.payload].attendeeInfo = null
        state.chimeMeetings[action.payload].meetingInfo = null
      }
    },
    updateConsultation: {
      reducer: (state, action: PayloadAction<{ consultationId: string; changes: UpdateConsultationDto }>) => {
        state.requestTracker.update.loading = true
      },
      prepare: (consultationId: string, changes: UpdateConsultationDto) => ({
        payload: {
          consultationId,
          changes,
        },
      }),
    },
    updateConsultationSuccess(state, action: PayloadAction<Consultation>) {
      const consultation = action.payload
      const existing = state.consultations.entities[consultation._id]
      const merged = safeMergeConsultation(consultation, existing)
      consultationAdapter.updateOne(state.consultations, { id: consultation._id, changes: merged })
      state.requestTracker.update.error = null
      state.requestTracker.update.loading = false
    },
    updateConsultationFailure(state, action) {
      state.requestTracker.update.error = action.payload
      state.requestTracker.update.loading = false
    },
    updateConsultationReason: {
      reducer: (state, action: PayloadAction<{ consultationReasonId: string; changes: UpdateConsultationReasonDto }>) => {
        state.requestTracker.updateConsultationReason.loading = true
      },
      prepare: (consultationReasonId: string, changes: UpdateConsultationReasonDto) => ({
        payload: {
          consultationReasonId,
          changes,
        },
      }),
    },
    updateConsultationReasonSuccess(state, action: PayloadAction<ConsultationReason>) {
      const consultationReason = action.payload
      consultationReasonAdapter.setOne(state.consultationReasons, consultationReason)
      state.requestTracker.updateConsultationReason.error = null
      state.requestTracker.updateConsultationReason.loading = false
    },
    updateConsultationReasonFailure(state, action) {
      state.requestTracker.updateConsultationReason.error = action.payload
      state.requestTracker.updateConsultationReason.loading = false
    },
    sendMesures: {
      reducer: (state, action: PayloadAction<{ consultationId: string; mesures: Consultation['mesures'] }>) => {
        state.requestTracker.update.loading = true
      },
      prepare: (consultationId: string, mesures: Consultation['mesures']) => ({
        payload: {
          consultationId,
          mesures,
        },
      }),
    },
    sendBiological: {
      reducer: (state, action: PayloadAction<{ consultationId: string; biological: addBiologicalDto }>) => {
        state.requestTracker.update.loading = true
      },
      prepare: (consultationId: string, biological: addBiologicalDto) => ({
        payload: {
          consultationId,
          biological,
        },
      }),
    },
    deleteBiological: {
      reducer: (state, action: PayloadAction<{ consultationId: string; biologicalId: string }>) => {
        state.requestTracker.update.loading = true
      },
      prepare: (consultationId: string, biologicalId: string) => ({
        payload: {
          consultationId,
          biologicalId,
        },
      }),
    },
    bookMeeting: {
      reducer: (state, action: PayloadAction<{ meeting: ICalendso }>) => {
        state.requestTracker.create.loading = true
      },
      prepare: (meeting: ICalendso) => ({
        payload: {
          meeting,
        },
      }),
    },
    saveMesures: {
      reducer: (state, action: PayloadAction<{ consultationId: string; csv: string }>) => {
        state.requestTracker.create.loading = true
      },
      prepare: (consultationId: string, csv: string) => ({
        payload: { consultationId, csv },
      }),
    },
    saveMesuresSuccess(state) {
      state.requestTracker.create.error = null
      state.requestTracker.create.loading = false
    },
    saveMesuresFailure(state, action) {
      state.requestTracker.create.error = action.payload
      state.requestTracker.create.loading = false
    },
    sendInvite: {
      reducer: (state, action: PayloadAction<{ consultationId: string; participant: InviteParticipantRequestParams }>) => {
        state.requestTracker.invite.loading = true
      },
      prepare: (consultationId: string, participant: InviteParticipantRequestParams) => ({
        payload: { consultationId, participant },
      }),
    },
    sendInviteSuccess(state, action: PayloadAction<Consultation>) {
      consultationAdapter.setOne(state.consultations, action.payload)
      state.requestTracker.invite.error = null
      state.requestTracker.invite.loading = false
    },
    sendInviteFailure(state, action) {
      state.requestTracker.invite.loading = false
      state.requestTracker.invite.error = action.payload
    },
    generateReport: {
      reducer: (state, action: PayloadAction<{ consultationId: string; reportValues: Record<string, any> }>) => {
        state.requestTracker.create.loading = true
      },
      prepare: (consultationId: string, reportValues: Record<string, any>) => ({
        payload: { consultationId, reportValues },
      }),
    },
    generateReportSuccess(state) {
      state.requestTracker.create.error = null
      state.requestTracker.create.loading = false
    },
    generateReportFailure(state, action) {
      state.requestTracker.create.error = action.payload
      state.requestTracker.create.loading = false
    },
    receiveRpiIdRequest(state, action: PayloadAction<string>) { },
    generateFSP: {
      reducer: (state, action: PayloadAction<{ consultationId: string; acts: Act[] }>) => {
        state.requestTracker.create.loading = true
      },
      prepare: (consultationId: string, acts: Act[]) => ({
        payload: { consultationId, acts },
      }),
    },
    generateFSPSuccess(state) {
      state.requestTracker.create.error = null
      state.requestTracker.create.loading = false
    },
    generateFSPFailure(state, action) {
      state.requestTracker.create.error = action.payload
      state.requestTracker.create.loading = false
    },
    participantsList: {
      reducer(state, action: PayloadAction<{ consultationId: string; participants: Staff[] }>) {
        const { consultationId, participants } = action.payload
        state.participantsByConsultationId[consultationId] = participants
      },
      prepare: (consultationId: string, participants: Staff[]) => ({
        payload: { consultationId, participants },
      }),
    },
    searchParticipants: {
      reducer(state, action: PayloadAction<{ consultationId: string; participantType: string; searchTerm: string }>) {
        state.requestTracker.searchParticipants.loading = true
      },
      prepare: (searchTerm: string, participantType: string, consultationId: string) => ({
        payload: { consultationId, participantType, searchTerm },
      }),
    },
    searchParticipantsSuccess(state, action: PayloadAction<SearchedConsultationParticipant[]>) {
      state.searchParticipants.result.items = action.payload
      state.searchParticipants.result.total = action.payload?.length ?? 0
      state.requestTracker.searchParticipants.error = null
      state.requestTracker.searchParticipants.loading = false
    },
    searchParticipantsFailure(state, action) {
      state.requestTracker.searchParticipants.error = action.payload
      state.requestTracker.searchParticipants.loading = false
    },
  },
  extraReducers: (builder) => {
    // for backward compatibility with consultation adapter
    builder
      .addMatcher(consultationApi.endpoints.getConsultation.matchFulfilled, (state, action) => {
        if (action.payload && action.payload._id) {
          consultationAdapter.upsertOne(state.consultations, action.payload)
        }
      })
      .addMatcher(consultationApi.endpoints.updateConsultation.matchFulfilled, (state, action) => {
        if (action.payload && action.payload._id) {
          const existing = state.consultations.entities[action.payload._id]
          const merged = safeMergeConsultation(action.payload, existing)
          consultationAdapter.updateOne(state.consultations, { id: action.payload._id, changes: merged })
        }
      })
      .addMatcher(consultationApi.endpoints.getConsultationsByGroup.matchFulfilled, (state, action) => {
        if (Array.isArray(action.payload)) {
          consultationAdapter.upsertMany(state.consultations, action.payload)
        }
      })
  },
})

// actions for socket to emit event
export const consultationCreatedSocketEvent = createAction<Consultation>(`${consultationModel.reducerName}/consultationCreatedSocketEvent`)
export const consultationUpdatedSocketEvent = createAction<Consultation>(`${consultationModel.reducerName}/consultationUpdatedSocketEvent`)
export const joinConsultationSocketEvent = createAction<string>(`${consultationModel.reducerName}/joinConsultationSocketEvent`)
export const leaveConsultationSocketEvent = createAction<string>(`${consultationModel.reducerName}/leaveConsultationSocketEvent`)
export const requestRpiIdSocketEvent = createAction<string>(`${consultationModel.reducerName}/requestRpiIdSocketEvent`)
export const sendRpiIdSocketEvent = createAction<{ consultationId: string; rpiId: string }>(`${consultationModel.reducerName}/sendRpiIdSocketEvent`)
export const sendMesuresSocketEvent = createAction<{ consultationId: string; mesures: Consultation['mesures'] }>(
  `${consultationModel.reducerName}/sendMesuresSocketEvent`
)

export const recordingConsentRequestedSocketEvent = createAction<{ consultationId: string }>(
  `${consultationModel.reducerName}/recordingConsentRequestedSocketEvent`
)
export const recordingConsentResponseReceived = createAction<{ consultationId: string; accepted: boolean }>(
  `${consultationModel.reducerName}/recordingConsentResponseReceived`
)

export const {
  clearNewlyCreatedConsultationId,
  createConsultation,
  createConsultationSuccess,
  createConsultationFailure,
  createConsultationReason,
  createConsultationReasonSuccess,
  createConsultationReasonFailure,
  deleteConsultationReason,
  deleteConsultationReasonSuccess,
  deleteConsultationReasonFailure,
  endConsultation,
  endConsultationSuccess,
  endConsultationFailure,
  getConsultationReasons,
  getConsultationReasonsSuccess,
  getConsultationReasonsFailure,
  getConsultation,
  getConsultationSuccess,
  getConsultationFailure,
  getConsultations,
  getConsultationsSuccess,
  getConsultationsFailure,
  getRpiMeetingId,
  getRpiMeetingIdSuccess,
  getRpiMeetingIdFailure,
  getStunServer,
  setStunServer,
  joinMeeting,
  joinMeetingSuccess,
  joinMeetingFailure,
  leaveMeeting,
  updateConsultation,
  updateConsultationSuccess,
  updateConsultationFailure,
  updateConsultationReason,
  updateConsultationReasonSuccess,
  updateConsultationReasonFailure,
  sendMesures,
  sendBiological,
  deleteBiological,
  bookMeeting,
  saveMesures,
  saveMesuresSuccess,
  saveMesuresFailure,
  sendInvite,
  sendInviteSuccess,
  sendInviteFailure,
  generateReport,
  generateReportSuccess,
  generateReportFailure,
  receiveRpiIdRequest,
  generateFSP,
  generateFSPSuccess,
  generateFSPFailure,
  participantsList,
  searchParticipants,
  searchParticipantsSuccess,
  searchParticipantsFailure,
} = consultationSlice.actions

export default consultationSlice.reducer
